import React, { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import axios from "axios";
import { DndProvider } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import { Alert, Box } from "@mui/material";

import { getSavedProjects } from "../../../../../store/actions/dataActions";
import {
  getCombineFields,
  buildPaisFieldTag,
  PAIS_COLUMN_DEFINITIONS,
} from "../../../../permitForms/permitFormHelpers";
import { categoryData as paisCategoryData } from "../../../../../data/pais/paisData";
import PaisTagAssignPopover from "./components/PaisTagAssignPopover";
import TableTagPdfPreview from "./components/TableTagPdfPreview";
import PreviewFormDialog from "./components/preview/PreviewFormDialog";
import PDFEditorHeader from "./components/PDFEditorHeader";
import PDFEditorControlsBar from "./components/PDFEditorControlsBar";
import PDFEditorLeftPanel from "./components/PDFEditorLeftPanel";
import ObjectInspectorDrawer from "./components/ObjectInspectorDrawer";
import PDFCanvas from "./components/PDFCanvas";
import AddFieldDrawer from "./components/AddFieldDrawer";
import { usePdfEditorState } from "./hooks/usePdfEditorState";
import FormDetailsCard from "../components/FormDetails";
import {
  getDocumentDisplayRotation,
  getDocumentSize,
} from "./utils/documentOrientation";
import {
  buildVisibleTableRows,
  getTableSelectionMode,
} from "./utils/tableFieldLayout";
import { isFormEditLockLostError, renewFormEditLock } from "./formEditLockApi";

const PDFFormEditor = ({
  formData,
  onClose,
  onFinalizeSuccess,
  onSave,
  onNotify,
  isOpen = false,
  loading,
  isNew = false,
  onError,
  onSuccess,
  setFormData,
  jurisdictionOptions = [],
  finalizedEditMode = false,
  finalizedEditInitialView = "preview",
  externalEditLockLost = false,
  onRecoverEditLock,
}) => {
  const currentSession = useSelector((state) => state.sessions.currentSession);
  const dispatch = useDispatch();

  const [objectInspectorOpen, setObjectInspectorOpen] = useState(false);
  const [addFieldOpen, setAddFieldOpen] = useState(false);
  const [blockPanelOpen, setBlockPanelOpen] = useState(true);
  const [editableTitle, setEditableTitle] = useState("");

  const [fields, setFields] = useState([]);
  const [fieldNavigationTarget, setFieldNavigationTarget] = useState(null);

  const [openPreviewDialog, setOpenPreviewDialog] = useState(false);
  const [openSettingsDialog, setOpenSettingsDialog] = useState(false);
  const [isLoadingUpdateSetting, setIsLoadingUpdateSetting] = useState(false);
  const [autoTagLoading, setAutoTagLoading] = useState(false);
  const [editLockLost, setEditLockLost] = useState(false);
  const lockLossNotifiedRef = useRef(false);
  const lockRenewalPromiseRef = useRef(null);
  const onErrorRef = useRef(onError);

  useEffect(() => {
    onErrorRef.current = onError;
  }, [onError]);

  const [paisPopoverOpen, setPaisPopoverOpen] = useState(false);
  const [savedPaisColumnOrder, setSavedPaisColumnOrder] = useState([]);
  const documentSize = getDocumentSize(formData);
  const displayRotation = getDocumentDisplayRotation(documentSize);

  const {
    currentProcessingFile,
    setCurrentProcessingFile,
    numPages,
    pageNumber,
    scale,
    pdfInput,
    base64File,
    resizing,
    setResizing,
    moving,
    setMoving,
    isDrawing,
    setIsDrawing,
    isSelecting,
    setIsSelecting,
    rect,
    setRect,
    selectionRect,
    setSelectionRect,
    freeSelectStartPoint,
    setFreeSelectStartPoint,
    startPoint,
    setStartPoint,
    initialMousePosition,
    setInitialMousePosition,
    initialInputSize,
    setInitialInputSize,
    inputPosition,
    setInputPosition,
    currCursor,
    setCurrCursor,
    resizeDirection,
    setResizeDirection,
    selectToolActive,
    typingToolActive,
    focusedField,
    setFocusedField,
    highlightFields,
    setHighlightFields,
    textOverlayDisplayMode,
    setTextOverlayDisplayMode,
    unassignedFieldNames,
    pdfContainerRef,
    pdfDocumentRef,
    inputRef,
    childInputRef,
    fixedInputs,
    handleFieldChange,
    onDocumentLoadSuccess,
    onPageLoadSuccess,
    handleScaleChange,
    handlePageChange,
    handleSave,
    handleClose,
    activeEditorTool,
    handleEditorToolSelection,
    pdfScrollContainerRef,
    pushHistory,
    undo,
    redo,
    canRedo,
    canUndo,
    textSelectionMode,
    setTextSelectionMode,
  } = usePdfEditorState({
    formData,
    isOpen,
    onClose,
    onSave,
    onNotify,
    editableTitle,
    displayRotation,
  });

  const combineFields = getCombineFields(
    currentProcessingFile?.document?.tempFields?.formFields || [],
    fields,
  );

  const selectedTableRows = React.useMemo(() => {
    const selectedTableFields =
      currentProcessingFile?.document?.tempFields?.selectedExtraFields?.filter(
        (field) => !field?.name?.startsWith("block__"),
      ) || [];

    return {
      fields: selectedTableFields,
      rows: buildVisibleTableRows(
        selectedTableFields,
        currentProcessingFile?.document?.tempFields?.currentDocument || {},
      ),
    };
  }, [currentProcessingFile]);
  const selectedTableMode = React.useMemo(
    () =>
      getTableSelectionMode(
        selectedTableRows.rows,
        selectedTableRows.fields.length,
      ),
    [selectedTableRows],
  );

  const showPaisTableButton = React.useMemo(() => {
    if (activeEditorTool !== "edit") {
      return false;
    }

    return selectedTableMode.supportsGenericTableTagging;
  }, [activeEditorTool, selectedTableMode]);

  const tableTagSelectionMode = React.useMemo(() => {
    if (activeEditorTool !== "edit") {
      return {
        supportsPaisTableTagging: false,
        supportsGenericTableTagging: false,
      };
    }

    return selectedTableMode;
  }, [activeEditorTool, selectedTableMode]);

  const paisSelectedTableRows = selectedTableRows.rows;

  const paisDetectedColumnCount = React.useMemo(
    () =>
      paisSelectedTableRows.reduce(
        (max, row) => Math.max(max, row.fields?.length || 0),
        0,
      ),
    [paisSelectedTableRows],
  );

  const getFormFields = useCallback(async () => {
    try {
      await dispatch(getSavedProjects());

      const res = await axios.post(
        `${process.env.REACT_APP_BASE_URL}/nocApi/form-fields/list`,
        { currentSession },
        { withCredentials: true },
      );

      if (res.data.success) {
        const fields = res.data.data?.fields;
        setFields(fields);
      }
    } catch (error) {
      console.error(error);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentSession]);

  useEffect(() => {
    if (externalEditLockLost) {
      setEditLockLost(true);
    }
  }, [externalEditLockLost]);

  useEffect(() => {
    getFormFields();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleNavigateToTaggedField = useCallback(
    (taggedField) => {
      if (!taggedField?.field_id) return;

      const findFieldById = (fieldId, fieldList = []) => {
        for (const field of fieldList) {
          if (field?.field_id === fieldId) {
            return field;
          }

          const childMatch = findFieldById(fieldId, field?.children || []);
          if (childMatch) return childMatch;
        }

        return null;
      };

      const assignedField = findFieldById(taggedField.field_id, fields);
      const parentField = assignedField?.parent_field_id
        ? findFieldById(assignedField.parent_field_id, fields)
        : null;
      const navigationField = parentField || assignedField;

      setBlockPanelOpen(true);
      setFieldNavigationTarget({
        fieldId: navigationField?.field_id || taggedField.field_id,
        searchQuery:
          navigationField?.field_name || navigationField?.field_label || "",
        tabValue: navigationField?.is_default ? "3" : "2",
        requestId: Date.now(),
      });
    },
    [fields],
  );

  const handleFieldNavigationComplete = useCallback((requestId) => {
    setFieldNavigationTarget((currentTarget) =>
      currentTarget?.requestId === requestId ? null : currentTarget,
    );
  }, []);

  React.useEffect(() => {
    setEditableTitle(
      formData?.guessedFormDetails?.label_guess ||
        formData?.formName ||
        formData?.fileName ||
        "Untitled Form",
    );
  }, [formData]);

  const finalizedEditorReady = Boolean(currentProcessingFile);

  useEffect(() => {
    if (!isOpen) {
      setOpenPreviewDialog(false);
      setOpenSettingsDialog(false);
      return;
    }

    if (!finalizedEditMode || !finalizedEditorReady) return;

    const shouldOpenSettings = finalizedEditInitialView === "settings";
    setOpenPreviewDialog(!shouldOpenSettings);
    setOpenSettingsDialog(shouldOpenSettings);
  }, [
    finalizedEditInitialView,
    finalizedEditMode,
    finalizedEditorReady,
    isOpen,
  ]);

  const editLockFormId = formData?.editLock?.formId || "";
  const editLockId = formData?.editLock?.lockId || "";
  const editLockHeartbeatSeconds = formData?.editLock?.heartbeatSeconds || 30;

  const renewCurrentEditLock = useCallback(
    async ({ attempts = 3 } = {}) => {
      if (!editLockFormId || !editLockId) return true;
      if (lockRenewalPromiseRef.current) return lockRenewalPromiseRef.current;

      const renewalPromise = renewFormEditLock({
        currentSession,
        formId: editLockFormId,
        lockId: editLockId,
        attempts,
      })
        .then(() => true)
        .catch(async (error) => {
          if (isFormEditLockLostError(error)) {
            if (onRecoverEditLock) {
              try {
                const recovered = await onRecoverEditLock();
                if (recovered) return recovered;
              } catch (recoveryError) {
                error = recoveryError;
              }
            }
            setEditLockLost(true);
            if (!lockLossNotifiedRef.current) {
              lockLossNotifiedRef.current = true;
              onErrorRef.current?.(
                error?.response?.data?.message ||
                  "The editor lock was lost. Saving has been disabled; close and reopen the form.",
              );
            }
          } else {
            console.warn(
              "Temporary form editor lock heartbeat failure:",
              error,
            );
          }
          return false;
        })
        .finally(() => {
          if (lockRenewalPromiseRef.current === renewalPromise) {
            lockRenewalPromiseRef.current = null;
          }
        });

      lockRenewalPromiseRef.current = renewalPromise;
      return renewalPromise;
    },
    [currentSession, editLockFormId, editLockId, onRecoverEditLock],
  );

  useEffect(() => {
    if (!isOpen || !editLockFormId || !editLockId) {
      setEditLockLost(false);
      lockLossNotifiedRef.current = false;
      return undefined;
    }

    const heartbeat = () => {
      renewCurrentEditLock();
    };

    heartbeat();
    const interval = window.setInterval(
      heartbeat,
      Math.max(5, Number(editLockHeartbeatSeconds) || 30) * 1000,
    );

    const handleOnline = () => heartbeat();
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") heartbeat();
    };
    window.addEventListener("online", handleOnline);
    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      window.clearInterval(interval);
      window.removeEventListener("online", handleOnline);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [
    editLockFormId,
    editLockHeartbeatSeconds,
    editLockId,
    isOpen,
    renewCurrentEditLock,
  ]);

  useEffect(() => {
    if (!showPaisTableButton) {
      setPaisPopoverOpen(false);
    }
  }, [showPaisTableButton]);

  const handleOpenPaisPopover = useCallback(() => {
    if (!showPaisTableButton) {
      return;
    }

    setPaisPopoverOpen(true);
  }, [showPaisTableButton]);

  const handleRemoveHiddenFieldFromSelection = () => {
    setCurrentProcessingFile((prevFile) => {
      return {
        ...prevFile,
        document: {
          ...prevFile.document,
          tempFields: {
            ...prevFile.document.tempFields,
            selectedExtraFields:
              prevFile.document.tempFields.selectedExtraFields.filter(
                (f) => !f?.hidden,
              ),
            selectedFormField: prevFile.document.tempFields.selectedFormField
              ?.hidden
              ? null
              : prevFile.document.tempFields.selectedFormField,
          },
        },
      };
    });
  };

  const handleToggleObjectInspector = () => {
    if (addFieldOpen && !objectInspectorOpen) {
      setAddFieldOpen(false);
    }
    if (objectInspectorOpen) {
      handleRemoveHiddenFieldFromSelection();
    }
    setObjectInspectorOpen((prev) => !prev);
  };

  const handleToggleAddField = () => {
    if (objectInspectorOpen) {
      handleRemoveHiddenFieldFromSelection();
    }
    if (objectInspectorOpen && !addFieldOpen) {
      setObjectInspectorOpen(false);
    }
    setAddFieldOpen((prev) => !prev);
  };

  const handleUpdateSettings = async (settings) => {
    const formId = formData?.formId || formData?.form?.form_id;

    if (!formId) {
      onError("No form ID found.");
      return;
    }

    if (!settings) {
      onError("Settings data is missing.");
      return;
    }

    setIsLoadingUpdateSetting(true);

    try {
      const renewedLock = await renewCurrentEditLock();
      if (!renewedLock) {
        if (!editLockLost) {
          onErrorRef.current?.(
            "The edit session could not be confirmed because of a connection problem. Your changes remain in the editor; check your connection and try again.",
          );
        }
        return;
      }

      const { documentLabel, ...documentSettings } = settings;
      const response = await axios.post(
        `${process.env.REACT_APP_BASE_URL}/nocApi/forms/update-settings`,
        {
          currentSession,
          formId,
          settings: documentSettings,
          syncedFormEditToken: formData?.syncedFormEditToken || "",
          lockId: renewedLock?.lockId || formData?.editLock?.lockId || "",
        },
        { withCredentials: true },
      );

      if (!response?.data?.success) {
        onError(response?.data?.message || "Failed to update form settings");
        return;
      }

      const updatedSettings = response?.data?.data?.updated_settings || {};
      const nextDocumentLabel = documentLabel?.trim();

      if (nextDocumentLabel && nextDocumentLabel !== editableTitle) {
        const labelUpdated = await handleSave(false, {
          title: nextDocumentLabel,
          silent: true,
        });

        if (!labelUpdated) return;
        setEditableTitle(nextDocumentLabel);
      }

      setFormData((prev) => ({
        ...(prev || {}),
        ...(response?.data?.data || {}),
        settings: {
          ...(prev?.settings || {}),
          ...updatedSettings,
        },
        ...(documentSettings?.permitType
          ? {
              selected_permit_type: documentSettings.permitFormId
                ? documentSettings.permitType
                : "",
            }
          : {}),
      }));

      onSuccess("Form settings updated successfully");
      if (finalizedEditMode) {
        if (onFinalizeSuccess) {
          await onFinalizeSuccess();
        } else {
          handleClose();
        }
      } else {
        setOpenSettingsDialog(false);
      }
    } catch (error) {
      console.error("Form settings update error:", error);
      if (
        error?.response?.status === 409 &&
        error?.response?.data?.code === "FORM_EDIT_LOCK_LOST"
      ) {
        setEditLockLost(true);
      }
      onError(
        error?.response?.data?.message ||
          "Failed to update form settings. Please try again.",
      );
    } finally {
      setIsLoadingUpdateSetting(false);
    }
  };

  const editorBusy = loading || autoTagLoading || editLockLost;

  const handleAutoTagFields = useCallback(async () => {
    const formId =
      formData?.form_id || formData?.formId || formData?.form?.form_id || "";
    const instances =
      currentProcessingFile?.document?.tempFields?.formFields || [];

    if (!formId) {
      onError?.("No form ID found.");
      return;
    }

    try {
      setAutoTagLoading(true);

      const response = await axios.post(
        `${process.env.REACT_APP_BASE_URL}/nocApi/form-field-instances/auto-tag-fields`,
        {
          form_id: formId,
          instances,
        },
        { withCredentials: true },
      );

      if (!response?.data?.success) {
        onError?.(response?.data?.message || "Failed to auto tag fields");
        return;
      }

      const matchedInstances = response?.data?.data?.matched || [];

      if (!Array.isArray(matchedInstances)) {
        onError?.("Invalid auto tag response received.");
        return;
      }

      if (matchedInstances.length === 0) {
        onSuccess?.("Auto tag completed successfully");
        return;
      }

      setCurrentProcessingFile((prevFile) => {
        if (!prevFile?.document?.tempFields) return prevFile;

        const selectedField = prevFile.document.tempFields.selectedFormField;
        const selectedExtras =
          prevFile.document.tempFields.selectedExtraFields || [];

        const matchedById = new Map(
          matchedInstances
            .filter((field) => field?.field_instance_id)
            .map((field) => [field.field_instance_id, field]),
        );
        const normalizedInstances = (
          prevFile.document.tempFields.formFields || []
        ).map((field) => {
          const matchedField = matchedById.get(field?.field_instance_id);

          if (!matchedField) {
            return field?.type === "PDFTextField"
              ? { ...field, autoFontSize: true }
              : field;
          }

          const mergedField = {
            ...field,
            ...matchedField,
            field_id: matchedField.field_id,
            field_label: matchedField.matched_to,
            label: matchedField.matched_to,
            name: field.name,
          };

          return mergedField?.type === "PDFTextField"
            ? { ...mergedField, autoFontSize: true }
            : mergedField;
        });

        const matchField = (candidate) =>
          normalizedInstances.find((field) =>
            candidate?.field_instance_id
              ? field?.field_instance_id === candidate.field_instance_id
              : field?.name === candidate?.name,
          );

        return {
          ...prevFile,
          document: {
            ...prevFile.document,
            tempFields: {
              ...prevFile.document.tempFields,
              formFields: normalizedInstances,
              selectedFormField: matchField(selectedField) || null,
              selectedExtraFields: selectedExtras
                .map((field) => matchField(field))
                .filter(Boolean),
            },
          },
        };
      });

      onSuccess?.("Auto tag completed successfully");
    } catch (error) {
      console.error("Auto tag fields error:", error);
      onError?.(
        error?.response?.data?.message ||
          "Failed to auto tag fields. Please try again.",
      );
    } finally {
      setAutoTagLoading(false);
    }
  }, [
    currentProcessingFile,
    formData,
    onError,
    onSuccess,
    setCurrentProcessingFile,
  ]);

  const handleObjectInspectorFieldChange = useCallback(
    (fieldKey, value) => {
      pushHistory?.();
      setCurrentProcessingFile((prev) => {
        const selectedTextItem =
          prev?.document?.tempFields?.selectedPdfTextItem || null;
        if (selectedTextItem) {
          const existingEdits = prev.document.tempFields.pdfTextEdits || [];
          const existingEdit = existingEdits.find(
            (edit) => edit.target_item_id === selectedTextItem.id,
          );

          let nextEdit = existingEdit
            ? { ...existingEdit }
            : {
                edit_id: `text_edit_${Date.now()}`,
                target_item_id: selectedTextItem.id,
                page: selectedTextItem.page,
                original_text: selectedTextItem.text,
                original_rect: { ...selectedTextItem.rect },
                rect: { ...selectedTextItem.rect },
                action: "replace",
                new_text: selectedTextItem.text,
              };

          if (fieldKey === "text") {
            nextEdit.new_text = value;
            nextEdit.action = value === "" ? "delete" : "replace";
          } else if (fieldKey === "action") {
            nextEdit.action = value;
          } else if (["x", "y", "width", "height"].includes(fieldKey)) {
            const rectKeyMap = { x: "X", y: "Y", width: "W", height: "H" };
            nextEdit.rect = {
              ...(nextEdit.rect || { ...selectedTextItem.rect }),
              [rectKeyMap[fieldKey]]: Number(value),
            };
            if (nextEdit.action !== "delete") {
              nextEdit.action = nextEdit.new_text ? "move" : "replace";
            }
          }

          const nextEdits = existingEdit
            ? existingEdits.map((edit) =>
                edit.target_item_id === selectedTextItem.id ? nextEdit : edit,
              )
            : [...existingEdits, nextEdit];

          return {
            ...prev,
            document: {
              ...prev.document,
              tempFields: {
                ...prev.document.tempFields,
                pdfTextEdits: nextEdits,
              },
            },
          };
        }

        if (!prev?.document?.tempFields?.selectedFormField) return prev;

        const selectedField = prev.document.tempFields.selectedFormField;
        const selectedFieldName = selectedField?.name;
        const selectedFieldId = selectedField?.id;

        if (fieldKey === "name") {
          const normalizedValue = String(value || "").trim();
          const duplicateField = (
            prev.document.tempFields.formFields || []
          ).find((field) => {
            const isSameField = selectedFieldId
              ? field?.id === selectedFieldId
              : field?.name === selectedFieldName;

            return (
              !isSameField && String(field?.name || "") === normalizedValue
            );
          });

          if (duplicateField) {
            onNotify?.(
              "error",
              "Field name already exists. Names must be unique across all pages.",
            );
            return prev;
          }

          value = normalizedValue;
        }

        const relatedOptionFields = combineFields
          ?.filter((f) =>
            ["selectGroup", "checkboxGroup"].includes(f?.field_type),
          )
          .find((field) =>
            field.options?.some((opt) => {
              if (opt.field_id === selectedField?.field_id) {
                return true;
              }
              return false;
            }),
          )
          ?.options?.map((opt) => (selectedFieldId ? opt?.id : opt?.name));

        const updatedFormFields = (
          prev.document.tempFields.formFields || []
        ).map((field) => {
          const isTargetField = selectedFieldId
            ? field?.id === selectedFieldId
            : field?.name === selectedFieldName;

          if (
            !isTargetField &&
            (fieldKey !== "parents" ||
              !relatedOptionFields?.includes(
                selectedFieldId ? field?.id : field?.name,
              ))
          )
            return field;

          return {
            ...field,
            ...(field.type === "PDFTextField" ? { autoFontSize: true } : {}),
            [fieldKey]: value,
          };
        });

        const nextSelectedField = {
          ...selectedField,
          ...(selectedField?.type === "PDFTextField"
            ? { autoFontSize: true }
            : {}),
          [fieldKey]: value,
        };

        return {
          ...prev,
          document: {
            ...prev.document,
            tempFields: {
              ...prev.document.tempFields,
              formFields: updatedFormFields,
              selectedFormField: nextSelectedField,
            },
          },
        };
      });
    },
    [onNotify, setCurrentProcessingFile, pushHistory, combineFields],
  );

  const handleMakeRowGroup = useCallback(() => {
    const tempFields = currentProcessingFile?.document?.tempFields;
    const id = Date.now();
    const selectedFieldIds = new Map();

    if (
      !tempFields?.selectedExtraFields ||
      tempFields.selectedExtraFields.length < 2
    ) {
      return;
    }

    const genericSelectedFields = tempFields.selectedExtraFields.filter(
      (field) => field?.field_id === "generic_table",
    );

    if (
      genericSelectedFields.length > 0 &&
      genericSelectedFields.length !== tempFields.selectedExtraFields.length
    ) {
      onNotify?.(
        "error",
        "Generic table fields cannot be grouped together with regular fields.",
      );
      return;
    }

    if (genericSelectedFields.length > 0) {
      const tableIds = new Set(
        genericSelectedFields.map((field) => field?.generic_table?.table_id),
      );
      const rowIndexes = new Set(
        genericSelectedFields.map((field) => field?.generic_table?.row_index),
      );
      const hasMissingGenericPosition = genericSelectedFields.some(
        (field) =>
          !field?.generic_table?.table_id ||
          !Number.isInteger(Number(field?.generic_table?.row_index)),
      );

      if (
        hasMissingGenericPosition ||
        tableIds.size !== 1 ||
        rowIndexes.size !== 1
      ) {
        onNotify?.(
          "error",
          "Select Generic table fields from the same table row to group them.",
        );
        return;
      }
    }

    tempFields?.selectedExtraFields?.forEach((f, index) => {
      selectedFieldIds.set(f?.name, `${id}_${index + 1}`);
    });

    const updatedFormFields = tempFields?.formFields?.map((f) => {
      if (selectedFieldIds?.has(f?.name)) {
        return {
          ...f,
          row: selectedFieldIds?.get(f?.name),
          ...(f?.field_id === "generic_table" && f?.generic_table
            ? {
                generic_table: {
                  ...f.generic_table,
                  row_group_disabled: false,
                },
              }
            : {}),
        };
      }
      return f;
    });

    const updatedSelectedFields = tempFields?.selectedExtraFields?.map((f) => ({
      ...f,
      row: selectedFieldIds?.get(f?.name),
      ...(f?.field_id === "generic_table" && f?.generic_table
        ? {
            generic_table: {
              ...f.generic_table,
              row_group_disabled: false,
            },
          }
        : {}),
    }));

    setCurrentProcessingFile((prevFile) => ({
      ...prevFile,
      document: {
        ...prevFile.document,
        tempFields: {
          ...prevFile.document.tempFields,
          formFields: [...updatedFormFields],
          selectedExtraFields: updatedSelectedFields,
          selectedFormField:
            prevFile.document.tempFields.selectedFormField &&
            selectedFieldIds.has(
              prevFile.document.tempFields.selectedFormField.name,
            )
              ? {
                  ...prevFile.document.tempFields.selectedFormField,
                  row: selectedFieldIds.get(
                    prevFile.document.tempFields.selectedFormField.name,
                  ),
                  ...(prevFile.document.tempFields.selectedFormField
                    ?.field_id === "generic_table" &&
                  prevFile.document.tempFields.selectedFormField?.generic_table
                    ? {
                        generic_table: {
                          ...prevFile.document.tempFields.selectedFormField
                            .generic_table,
                          row_group_disabled: false,
                        },
                      }
                    : {}),
                }
              : prevFile.document.tempFields.selectedFormField,
        },
      },
    }));

    onNotify("success", "Selected fields have been grouped into a single row");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentProcessingFile]);

  const handleRemoveFieldFromRow = useCallback(
    (row, isAll = false, displayLabel, options, genericRowGroupContext) => {
      const tempFields = currentProcessingFile?.document?.tempFields;

      if (genericRowGroupContext?.inherited) {
        const {
          tableId,
          rowIndex,
          columnIndex,
          groupedColumnIndexes = [],
        } = genericRowGroupContext;
        const normalizedGroupedColumnIndexes = groupedColumnIndexes.map(Number);
        const columnsToDisable = new Set(
          isAll || normalizedGroupedColumnIndexes.length <= 2
            ? normalizedGroupedColumnIndexes
            : [Number(columnIndex)],
        );
        const updateGenericRowGroupOverride = (field) => {
          const isTargetField =
            field?.generic_table?.table_id === tableId &&
            Number(field?.generic_table?.row_index) === Number(rowIndex) &&
            columnsToDisable.has(Number(field?.generic_table?.column_index));

          return isTargetField
            ? {
                ...field,
                row: "",
                generic_table: {
                  ...field.generic_table,
                  row_group_disabled: true,
                },
              }
            : field;
        };

        setCurrentProcessingFile((prevFile) => {
          const prevTempFields = prevFile?.document?.tempFields;
          const updatedFormFields = (prevTempFields?.formFields || []).map(
            updateGenericRowGroupOverride,
          );
          const findUpdatedField = (selectedField) =>
            updatedFormFields.find((field) =>
              selectedField?.field_instance_id
                ? field?.field_instance_id === selectedField.field_instance_id
                : field?.name === selectedField?.name,
            );

          return {
            ...prevFile,
            document: {
              ...prevFile.document,
              tempFields: {
                ...prevTempFields,
                formFields: updatedFormFields,
                selectedExtraFields: (
                  prevTempFields?.selectedExtraFields || []
                ).map(
                  (selectedField) =>
                    findUpdatedField(selectedField) || selectedField,
                ),
                selectedFormField:
                  findUpdatedField(prevTempFields?.selectedFormField) ||
                  prevTempFields?.selectedFormField,
              },
            },
          };
        });

        onNotify(
          "success",
          isAll || normalizedGroupedColumnIndexes.length <= 2
            ? "All fields have been removed from the row"
            : `${displayLabel} fields removed from the row`,
        );
        return;
      }

      const prefix = row?.split("_")?.[0];

      const optionsSet = options ? new Set(options) : null;

      const rowGroupFields = tempFields?.formFields?.filter((f) =>
        f?.row?.includes(prefix),
      );

      const updatedFormFields = tempFields?.formFields?.map((f) => {
        const match =
          isAll || rowGroupFields?.length <= 2
            ? f?.row?.includes(prefix)
            : row
              ? f?.row === row
              : optionsSet?.has(f?.name);

        return match ? { ...f, row: "" } : f;
      });

      const updatedSelectedFields = tempFields?.selectedExtraFields?.map(
        (f) => ({
          ...f,
          row: updatedFormFields?.find((field) => f?.name === field?.name)?.row,
        }),
      );

      setCurrentProcessingFile((prevFile) => {
        const wasSelectedFieldRemoved =
          prevFile.document.tempFields.selectedFormField &&
          (isAll
            ? prevFile.document.tempFields.selectedFormField.row?.includes(
                prefix,
              )
            : row
              ? prevFile.document.tempFields.selectedFormField.row === row
              : optionsSet?.has(
                  prevFile.document.tempFields.selectedFormField.name,
                ));

        return {
          ...prevFile,
          document: {
            ...prevFile.document,
            tempFields: {
              ...prevFile.document.tempFields,
              formFields: updatedFormFields,
              selectedExtraFields: updatedSelectedFields,
              selectedFormField: wasSelectedFieldRemoved
                ? {
                    ...prevFile.document.tempFields.selectedFormField,
                    row: "",
                  }
                : prevFile.document.tempFields.selectedFormField,
            },
          },
        };
      });

      onNotify(
        "success",
        isAll
          ? "All fields have been removed from the row"
          : `${displayLabel} fields removed from the row`,
      );
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [currentProcessingFile],
  );

  const handleGenericFieldsAssign = useCallback(
    ({ genericConfig }) => {
      const selected = selectedTableRows.fields;
      const rows = paisSelectedTableRows;

      if (!genericConfig || selected.length === 0 || rows.length === 0) return;

      const {
        label,
        rowMode,
        headerMode,
        rowCount,
        maxRowCount,
        columnCount,
        rowNames = [],
        columnNames = [],
        columnTypes = [],
      } = genericConfig;
      const detectedColumnCount = rows.reduce(
        (max, row) => Math.max(max, row.fields?.length || 0),
        0,
      );
      const hasNamedRows = headerMode === "named_rows";
      const isCustomRowTable = rowMode === "custom";
      const hasValidMaximum =
        maxRowCount === null ||
        maxRowCount === undefined ||
        (Number.isInteger(maxRowCount) && maxRowCount >= rows.length);
      const isValidConfiguration =
        label &&
        Number.isInteger(rowCount) &&
        rowCount === rows.length &&
        hasValidMaximum &&
        (!isCustomRowTable || !hasNamedRows) &&
        Number.isInteger(columnCount) &&
        columnCount === detectedColumnCount &&
        columnNames.length === columnCount &&
        columnNames.every(Boolean) &&
        columnTypes.length === columnCount &&
        columnTypes.every((type) => ["text", "number"].includes(type)) &&
        (!hasNamedRows ||
          (rowNames.length === rowCount && rowNames.every(Boolean)));

      if (!isValidConfiguration) {
        onNotify?.(
          "error",
          rowCount < rows.length
            ? `The configured row count must be at least ${rows.length} to cover the selected fields.`
            : "The generic table configuration does not match the selected fields.",
        );
        return;
      }

      const toTagSegment = (value, fallback) => {
        const normalized = String(value || "")
          .trim()
          .toLowerCase()
          .replace(/[^a-z0-9]+/g, "_")
          .replace(/^_+|_+$/g, "");

        return normalized || fallback;
      };
      const tableId = `generic_${toTagSegment(label, "table")}_${Date.now()}`;
      const tagByFieldKey = new Map();

      rows.forEach((row, rowIndex) => {
        row.fields.forEach((field, columnIndex) => {
          const fieldKey = field?.field_instance_id || field?.id || field?.name;
          const rowName = hasNamedRows ? rowNames[rowIndex] : null;
          const columnName = columnNames[columnIndex];
          const columnType = columnTypes[columnIndex];
          const rowTag = hasNamedRows
            ? toTagSegment(rowName, `row_${rowIndex + 1}`)
            : `row_${rowIndex + 1}`;
          const columnTag = toTagSegment(
            columnName,
            `column_${columnIndex + 1}`,
          );

          tagByFieldKey.set(fieldKey, {
            name: `${tableId}_${rowTag}_${columnTag}`,
            displayLabel: hasNamedRows
              ? [label, rowName, columnName].filter(Boolean).join(" - ")
              : [label, columnName, rowIndex + 1].join(" - "),
            metadata: {
              table_id: tableId,
              table_label: label,
              row_mode: rowMode,
              header_mode: headerMode,
              row_count: rowCount,
              max_row_count: isCustomRowTable ? (maxRowCount ?? null) : null,
              column_count: columnCount,
              row_index: rowIndex,
              column_index: columnIndex,
              row_name: rowName,
              column_name: columnName,
              field_type: columnType,
            },
            fieldType: columnType,
          });
        });
      });

      if (tagByFieldKey.size !== selected.length) {
        onNotify?.(
          "error",
          "Could not map every selected field to the generic table.",
        );
        return;
      }

      pushHistory?.();

      setCurrentProcessingFile((prevFile) => {
        const prevTempFields = prevFile?.document?.tempFields;
        const updatedFormFields = (prevTempFields?.formFields || []).map(
          (field) => {
            const fieldKey =
              field?.field_instance_id || field?.id || field?.name;
            const genericTag = tagByFieldKey.get(fieldKey);

            if (!genericTag) return field;

            return {
              ...field,
              name: genericTag.name,
              field_label: genericTag.displayLabel,
              label: genericTag.displayLabel,
              field_id: "generic_table",
              field_type: genericTag.fieldType,
              generic_table: genericTag.metadata,
            };
          },
        );

        return {
          ...prevFile,
          document: {
            ...prevFile.document,
            tempFields: {
              ...prevTempFields,
              formFields: updatedFormFields,
              selectedFormField: null,
              selectedExtraFields: [],
            },
          },
        };
      });

      onNotify?.("success", "Generic table fields tagged successfully");
    },
    [
      onNotify,
      paisSelectedTableRows,
      pushHistory,
      selectedTableRows.fields,
      setCurrentProcessingFile,
    ],
  );

  const handlePaisFieldsAssign = useCallback(
    ({ category, columnOrder = [], subCategoryOrder = [] }) => {
      const selected = selectedTableRows.fields;

      if (!category || selected.length === 0) return;

      const rows = paisSelectedTableRows;
      const dynamicColumnCodeMap = columnOrder.reduce((acc, column, index) => {
        const fixedCode = PAIS_COLUMN_DEFINITIONS[index]?.code;
        if (fixedCode && column?.label) {
          acc[column.label] = fixedCode;
        }
        return acc;
      }, {});
      const dynamicSubCategoryCodeMap = subCategoryOrder.reduce(
        (acc, subCategory, index) => {
          const fixedCode = category.subCategory?.[index]?.code;
          if (fixedCode && subCategory?.label) {
            acc[subCategory.label] = fixedCode;
          }
          return acc;
        },
        {},
      );
      const overflowRowCount = Math.max(
        0,
        rows.length - (category.subCategory?.length || 0),
      );

      const tagByFieldKey = new Map();
      const skipped = [];

      rows.forEach((row, rowIndex) => {
        const detectedSubCategoryMeaning =
          category.subCategory?.[rowIndex]?.label;
        const subCategoryCode = detectedSubCategoryMeaning
          ? dynamicSubCategoryCodeMap[detectedSubCategoryMeaning]
          : undefined;
        if (!subCategoryCode) {
          skipped.push(...row.fields);
          return;
        }
        row.fields.forEach((field, colIndex) => {
          const detectedColumnMeaning =
            PAIS_COLUMN_DEFINITIONS[colIndex]?.label;
          const columnCode = detectedColumnMeaning
            ? dynamicColumnCodeMap[detectedColumnMeaning]
            : undefined;
          if (!columnCode) {
            skipped.push(field);
            return;
          }
          const key = field?.field_instance_id || field?.id || field?.name;
          tagByFieldKey.set(
            key,
            buildPaisFieldTag(
              category.categoryCode,
              subCategoryCode,
              columnCode,
            ),
          );
        });
      });

      if (tagByFieldKey.size === 0) {
        onNotify?.(
          "error",
          "Could not map the selection to PAIS categories. Try selecting fewer rows/columns.",
        );
        return;
      }

      setSavedPaisColumnOrder(columnOrder);

      pushHistory?.();

      setCurrentProcessingFile((prevFile) => {
        const prevTempFields = prevFile?.document?.tempFields;
        const allFields = prevTempFields?.formFields || [];
        const selectedKeys = new Set(tagByFieldKey.keys());
        const takenTags = new Set(
          allFields
            .filter((f) => {
              const key = f?.field_instance_id || f?.id || f?.name;
              return !selectedKeys.has(key);
            })
            .map((f) => f?.name),
        );

        const updatedFormFields = allFields.map((field) => {
          const key = field?.field_instance_id || field?.id || field?.name;
          const newTag = tagByFieldKey.get(key);
          if (!newTag) return field;
          if (takenTags.has(newTag)) {
            skipped.push(field);
            return field;
          }
          return {
            ...field,
            name: newTag,
            field_label: newTag,
            label: newTag,
            field_id: "pais",
          };
        });

        return {
          ...prevFile,
          document: {
            ...prevFile.document,
            tempFields: {
              ...prevTempFields,
              formFields: updatedFormFields,
              selectedFormField: null,
              selectedExtraFields: [],
            },
          },
        };
      });

      onNotify?.(
        skipped.length > 0 ? "error" : "success",
        skipped.length > 0
          ? overflowRowCount > 0
            ? `${skipped.length} field(s) skipped. ${category.categoryLabel} supports ${category.subCategory?.length || 0} row(s), so ${overflowRowCount} extra selected row(s) could not be tagged.`
            : `${skipped.length} field(s) skipped (name already taken or mapping unavailable).`
          : "PAIS fields tagged successfully",
      );
    },
    [
      onNotify,
      paisSelectedTableRows,
      pushHistory,
      setSavedPaisColumnOrder,
      selectedTableRows.fields,
      setCurrentProcessingFile,
    ],
  );

  const handleTableFieldsAssign = useCallback(
    (configuration) => {
      if (configuration?.tableType === "generic") {
        handleGenericFieldsAssign(configuration);
        return;
      }

      handlePaisFieldsAssign(configuration);
    },
    [handleGenericFieldsAssign, handlePaisFieldsAssign],
  );

  if (!isOpen || !formData) return null;

  return (
    <DndProvider backend={HTML5Backend}>
      {!finalizedEditMode && (
        <Box
          sx={{
            position: "fixed",
            inset: 0,
            backgroundColor: "rgba(0, 0, 0, 0.8)",
            zIndex: 9999,
            display: "flex",
            flexDirection: "column",
          }}
          tabIndex={0}
        >
          <PDFEditorHeader
            title={editableTitle}
            onTitleChange={setEditableTitle}
            onSave={handleSave}
            onClose={handleClose}
            onFinalizeSuccess={onFinalizeSuccess}
            loading={editorBusy}
            setOpenPreviewDialog={setOpenPreviewDialog}
            formData={formData}
            isNew={isNew}
            openSettingsDialog={openSettingsDialog}
            setOpenSettingsDialog={setOpenSettingsDialog}
            onUpdate={handleUpdateSettings}
            editLockLost={editLockLost}
          />

          {editLockLost && (
            <Alert severity="error" variant="filled" sx={{ borderRadius: 0 }}>
              This editor no longer owns the inactive-form lock. Saving and
              editing are disabled. Close this editor and reopen the form to
              continue.
            </Alert>
          )}

          <Box
            sx={{
              display: "flex",
              flex: 1,
              overflow: "hidden",
              pointerEvents: editLockLost ? "none" : "auto",
              opacity: editLockLost ? 0.72 : 1,
            }}
          >
            <Box
              sx={{
                flex: 1,
                display: "flex",
                flexDirection: "column",
                backgroundColor: "#f5f5f5",
                overflow: "auto",
              }}
            >
              <PDFEditorControlsBar
                activeTool={activeEditorTool}
                onToolSelect={handleEditorToolSelection}
                scale={scale}
                handleScaleChange={handleScaleChange}
                highlightFields={highlightFields}
                setHighlightFields={setHighlightFields}
                textOverlayDisplayMode={textOverlayDisplayMode}
                setTextOverlayDisplayMode={setTextOverlayDisplayMode}
                numPages={numPages}
                pageNumber={pageNumber}
                handlePageChange={handlePageChange}
                objectInspectorOpen={objectInspectorOpen}
                onToggleObjectInspector={handleToggleObjectInspector}
                addFieldOpen={addFieldOpen}
                onToggleAddField={handleToggleAddField}
                blockPanelOpen={blockPanelOpen}
                onToggleBlockPanel={() => setBlockPanelOpen((prev) => !prev)}
                undoRedoStats={{ undo, redo, canRedo, canUndo }}
                textSelectionMode={textSelectionMode}
                setTextSelectionMode={setTextSelectionMode}
                loading={editorBusy}
                onAutoTagFields={handleAutoTagFields}
                handleMakeRowGroup={handleMakeRowGroup}
                selectedFieldsCount={
                  currentProcessingFile?.document?.tempFields
                    ?.selectedExtraFields?.length || 0
                }
                showPaisTableButton={showPaisTableButton}
                paisTableSelectActive={paisPopoverOpen}
                onTogglePaisTableSelect={handleOpenPaisPopover}
              />

              {showPaisTableButton && (
                <PaisTagAssignPopover
                  open={paisPopoverOpen}
                  onClose={() => setPaisPopoverOpen(false)}
                  categoryData={paisCategoryData}
                  detectedColumnCount={paisDetectedColumnCount}
                  detectedRowCount={paisSelectedTableRows.length}
                  columnDefinitions={PAIS_COLUMN_DEFINITIONS}
                  savedColumnOrder={savedPaisColumnOrder}
                  supportsPaisTableTagging={
                    tableTagSelectionMode.supportsPaisTableTagging
                  }
                  supportsGenericTableTagging={
                    tableTagSelectionMode.supportsGenericTableTagging
                  }
                  selectedCount={selectedTableRows.fields.length || 0}
                  pdfPreview={
                    <TableTagPdfPreview
                      file={base64File || pdfInput}
                      initialPage={pageNumber}
                      displayRotation={displayRotation}
                    />
                  }
                  onAssign={handleTableFieldsAssign}
                />
              )}

              <Box sx={{ display: "flex", flex: 1, minHeight: 0 }}>
                <PDFEditorLeftPanel
                  isOpen={blockPanelOpen}
                  currentProcessingFile={currentProcessingFile}
                  setCurrentProcessingFile={setCurrentProcessingFile}
                  fieldNavigationTarget={fieldNavigationTarget}
                  onFieldNavigationComplete={handleFieldNavigationComplete}
                  onNotify={onNotify}
                  loading={editorBusy}
                  fields={fields}
                  getFormFieldsList={getFormFields}
                />
                <PDFCanvas
                  base64File={base64File}
                  pdfInput={pdfInput}
                  pdfScrollContainerRef={pdfScrollContainerRef}
                  pdfContainerRef={pdfContainerRef}
                  pdfDocumentRef={pdfDocumentRef}
                  currentProcessingFile={currentProcessingFile}
                  setCurrentProcessingFile={setCurrentProcessingFile}
                  pageNumber={pageNumber}
                  scale={scale}
                  displayRotation={displayRotation}
                  selectToolActive={selectToolActive}
                  typingToolActive={typingToolActive}
                  highlightFields={highlightFields}
                  textOverlayDisplayMode={textOverlayDisplayMode}
                  focusedField={focusedField}
                  setFocusedField={setFocusedField}
                  currCursor={currCursor}
                  unassignedFieldNames={unassignedFieldNames}
                  fixedInputs={fixedInputs}
                  inputRef={inputRef}
                  childInputRef={childInputRef}
                  resizing={resizing}
                  setResizing={setResizing}
                  moving={moving}
                  setMoving={setMoving}
                  isDrawing={isDrawing}
                  setIsDrawing={setIsDrawing}
                  isSelecting={isSelecting}
                  setIsSelecting={setIsSelecting}
                  rect={rect}
                  setRect={setRect}
                  selectionRect={selectionRect}
                  setSelectionRect={setSelectionRect}
                  freeSelectStartPoint={freeSelectStartPoint}
                  setFreeSelectStartPoint={setFreeSelectStartPoint}
                  startPoint={startPoint}
                  setStartPoint={setStartPoint}
                  initialMousePosition={initialMousePosition}
                  setInitialMousePosition={setInitialMousePosition}
                  initialInputSize={initialInputSize}
                  setInitialInputSize={setInitialInputSize}
                  inputPosition={inputPosition}
                  setInputPosition={setInputPosition}
                  resizeDirection={resizeDirection}
                  setResizeDirection={setResizeDirection}
                  setCurrCursor={setCurrCursor}
                  onDocumentLoadSuccess={onDocumentLoadSuccess}
                  onPageLoadSuccess={onPageLoadSuccess}
                  handleFieldChange={handleFieldChange}
                  undoRedoStats={{ undo, redo, pushHistory }}
                  activeEditorTool={activeEditorTool}
                  handleEditorToolSelection={handleEditorToolSelection}
                  textSelectionMode={textSelectionMode}
                  formFields={fields}
                  onNavigateToTaggedField={handleNavigateToTaggedField}
                />

                <AddFieldDrawer
                  isOpen={addFieldOpen}
                  currentProcessingFile={currentProcessingFile}
                  setCurrentProcessingFile={setCurrentProcessingFile}
                  currCursor={currCursor}
                  formFields={fields}
                  onToggleObjectInspector={() => {
                    if (addFieldOpen && !objectInspectorOpen) {
                      setAddFieldOpen(false);
                    }
                    setObjectInspectorOpen((prev) => !prev);
                  }}
                />

                <ObjectInspectorDrawer
                  isOpen={objectInspectorOpen}
                  selectedField={
                    currentProcessingFile?.document?.tempFields
                      ?.selectedFormField
                  }
                  selectedTextItem={
                    currentProcessingFile?.document?.tempFields
                      ?.selectedPdfTextItem
                  }
                  pdfTextEdits={
                    currentProcessingFile?.document?.tempFields?.pdfTextEdits ||
                    []
                  }
                  allFields={combineFields}
                  documentFields={
                    currentProcessingFile?.document?.tempFields?.formFields ||
                    []
                  }
                  currentDocument={
                    currentProcessingFile?.document?.tempFields?.currentDocument
                  }
                  onFieldChange={handleObjectInspectorFieldChange}
                  loading={editorBusy}
                  textOverlayDisplayMode={textOverlayDisplayMode}
                  handleRemoveFieldFromRow={handleRemoveFieldFromRow}
                />
              </Box>
            </Box>
          </Box>
        </Box>
      )}
      {openPreviewDialog && (
        <PreviewFormDialog
          open={openPreviewDialog}
          setOpen={
            finalizedEditMode
              ? (nextOpen) => {
                  if (!nextOpen) handleClose();
                }
              : setOpenPreviewDialog
          }
          base64File={base64File}
          pdfInput={pdfInput}
          currentProcessingFile={currentProcessingFile}
          setCurrentProcessingFile={setCurrentProcessingFile}
          pageNumber={pageNumber}
          displayRotation={displayRotation}
          onDocumentLoadSuccess={onDocumentLoadSuccess}
          onPageLoadSuccess={onPageLoadSuccess}
          titleMetadata={{
            title: editableTitle,
            pageNumber: pageNumber,
            numPages: numPages,
            county:
              formData?.verified_details?.county ?? formData?.county ?? "",
            municipality:
              formData?.verified_details?.municipality ??
              formData?.municipality ??
              "",
          }}
          fields={fields}
          numPages={numPages}
          handlePageChange={handlePageChange}
          finalizedEditMode={finalizedEditMode}
          onSave={finalizedEditMode ? () => handleSave(false) : undefined}
        />
      )}
      {openSettingsDialog && (
        <FormDetailsCard
          selectedFile={formData}
          onSubmit={handleUpdateSettings}
          onCancel={
            finalizedEditMode ? handleClose : () => setOpenSettingsDialog(false)
          }
          uploading={isLoadingUpdateSetting}
          jurisdictionOptions={jurisdictionOptions || undefined}
          initialValues={{
            ...(formData?.updated_settings || formData?.guessedFormDetails),
            docSize:
              formData?.updated_settings?.docSize ||
              formData?.guessedFormDetails?.docSize ||
              formData?.form?.doc?.size,
            documentLabel:
              editableTitle || formData?.formName || formData?.fileName || "",
          }}
          isAlreadyUploaded={true}
          finalizedEditMode={finalizedEditMode}
        />
      )}
    </DndProvider>
  );
};

export default PDFFormEditor;
